import { ref, watch } from 'vue'
import client from '@/composables/homebaseClient'

export default function useHomebaseCollectionRequests(initialParams = {}) {
    const requests = ref([])
    const users = ref([])
    const loading = ref(false)
    const error = ref(null)

    const params = ref({
        status: 'requested',
        householdId: '',
        assignedTo: '',
        limit: 100,
        sortBy: 'requestedAt',
        sortDir: 'desc',
        ...initialParams
    })

    const toQuery = () => {
        const q = new URLSearchParams()
        if (params.value.status) q.set('status', params.value.status)
        if (params.value.householdId) q.set('householdId', params.value.householdId)
        if (params.value.assignedTo) q.set('assignedTo', params.value.assignedTo)
        if (params.value.limit) q.set('limit', String(params.value.limit))
        if (params.value.sortBy) q.set('sortBy', params.value.sortBy)
        if (params.value.sortDir) q.set('sortDir', params.value.sortDir)
        return q.toString() ? `?${q.toString()}` : ''
    }

    const fetchRequests = async () => {
        loading.value = true
        error.value = null
        try {
            const res = await client.get(`/collection-requests${toQuery()}`)
            requests.value = Array.isArray(res.data) ? res.data : []
        } catch (err) {
            error.value = err.detail || 'Failed to load collection requests'
        } finally {
            loading.value = false
        }
    }

    const fetchUsers = async () => {
        try {
            const res = await client.get('/users?limit=100')
            users.value = Array.isArray(res.data) ? res.data : []
        } catch (err) {
            console.error('Failed to load users:', err)
        }
    }

    const assignRequest = async (requestId, assignedTo) => {
        try {
            const res = await client.patch(`/collection-requests/${requestId}/assign`, {
                assignedTo
            })
            await fetchRequests() // refresh list
            return res.data
        } catch (err) {
            throw new Error(err.detail || 'Failed to assign request')
        }
    }

    const updateRequestStatus = async (requestId, status, note = '', updatedBy = '') => {
        try {
            const body = { status }
            if (note) body.note = note
            if (updatedBy) body.updatedBy = updatedBy

            const res = await client.patch(`/collection-requests/${requestId}/status`, body)
            await fetchRequests() // refresh list
            return res.data
        } catch (err) {
            throw new Error(err.detail || 'Failed to update request status')
        }
    }

    const createManualRequest = async (requestData) => {
        try {
            const res = await client.post('/collections/start-manual', requestData)
            await fetchRequests() // refresh list
            return res.data
        } catch (err) {
            throw new Error(err.detail || 'Failed to create manual request')
        }
    }

    const checkPendingRequest = async (containerId, householdId) => {
        try {
            const res = await client.get(`/collection-requests/check-pending?containerId=${containerId}&householdId=${householdId}`)
            return res.data.pending
        } catch (err) {
            console.error('Failed to check pending request:', err)
            return false
        }
    }

    // Auto-fetch when params change
    watch(params, fetchRequests, { deep: true })

    return {
        requests,
        users,
        loading,
        error,
        params,
        fetchRequests,
        fetchUsers,
        assignRequest,
        updateRequestStatus,
        createManualRequest,
        checkPendingRequest
    }
}
